import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomDateInput from "../../../../../../../../common/onboarding/third-step/set-goal-modal/common/CustomDateInput";
import DatePicker from "react-datepicker";
import { Form, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../../../../store";
import BaseSelect from "react-select";
import CreatableSelect from "react-select/creatable";
import { CoachInfo, ReactSelectOption, TeacherInfo } from "../../../../../../../../../store/onboarding/types";
import {
  createAttendance,
  createLessonNotes,
  updateLessonNotes,
} from "../../../../../../../../../store/onboarding/cases/actions";
import {
  AttachedResource,
  ChecklistItem,
  Intervention,
  InterventionGroup,
  InterventionPlanStates,
  LessonNoteRecordTag,
  LessonNoteRecordTagType,
  LessonNoteRecordTagTypeDisplayedValues,
  LessonNotes,
  StudentGoal,
} from "../../../../../../../../../store/onboarding/cases/types";
import { useLoading } from "../../../../../../../../../utils/hooks/useLoading";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { getFullName } from "../../../../../../../../../utils/NamesUtils";
import _ from "lodash";
import AttendanceStudentsList from "../attendance-tab/AttendanceStudentsList";
import AddResourcesForm from "../../../../../explore-and-learn/intervention-library/new-intervention-modal/intervention-steps-form/AddResourcesForm";
import { UserAssignmentRole, UserInfo } from "../../../../../../../../../store/auth/types";
import FixRequiredSelect from "../../../../../../../../common/required-select/FixRequiredSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faInfoCircle, faMicrophone } from "@fortawesome/free-solid-svg-icons";
import CustomDateBoxInput from "../../../../common/CustomDateBoxInput";
import { AxiosError } from "axios";

type Props = {
  show?: boolean;
  onHideForm: () => void;
  selectedLessonNotes: LessonNotes | undefined;
  selectedGroup: InterventionGroup;
};

const ID_GENERAL_NOTES = -1;
const MAX_LENGTH_TEXT_AREA = 3000;

const Select = (
  props: JSX.IntrinsicAttributes &
    JSX.IntrinsicClassAttributes<FixRequiredSelect> &
    Readonly<any> &
    Readonly<{ children?: React.ReactNode }>
) => (
  <FixRequiredSelect
    {...props}
    SelectComponent={BaseSelect}
    options={props.options}
  />
);

const LessonNotesUpsertForm: FunctionComponent<Props> = (props) => {
  const { show, onHideForm, selectedLessonNotes, selectedGroup } = props;
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>("");

  const recognitionRef = useRef<any>(null);

  const [isListening, setIsListening] = useState(false);
  const [speechStatus, setSpeechStatus] = useState("");
  const [micSupported, setMicSupported] = useState(true);
  const [interimText, setInterimText] = useState("");

  const isMicDisabled = notes.length >= MAX_LENGTH_TEXT_AREA;

  // Detect Firefox and Safari — mic feature is intentionally disabled for these browsers
  const isFirefox = () => {
    try {
      return /Firefox\//.test(navigator.userAgent) && !/Seamonkey\//.test(navigator.userAgent);
    } catch {
      return false;
    }
  };

  const isSafari = () => {
    try {
      // Check if it's Safari but NOT Chrome (CriOS is Chrome on iOS)
      const ua = navigator.userAgent;
      const isSafariBrowser = /Safari\//.test(ua);
      const isNotChrome = !/Chrome|CriOS|Chromium|Edg\//.test(ua);
      return isSafariBrowser && isNotChrome;
    } catch {
      return false;
    }
  };

  // Check if device is mobile/tablet
  const isMobileOrTablet = () => {
    try {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    } catch {
      return false;
    }
  };

  const selectedInterventionGroup = useSelector(
    (s: ApplicationState) => s.cases.selectedInterventionGroup
  );

  let mergedInterventions: Intervention[] = useMemo(() => {
    let mergedInterventions: Intervention[] = [];
    if(selectedLessonNotes) {
      mergedInterventions = [...selectedInterventionGroup!.interventions, ...selectedInterventionGroup!.archived_interventions]
    }
    return Object.values(
      mergedInterventions.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {} as Record<number, Intervention>)
    );
  },[selectedLessonNotes])

  const interventions = useMemo((): Intervention[] => {
    if(selectedLessonNotes) {
      
      let activeIntervention: Intervention[] = 
        selectedInterventionGroup?.interventions
        .filter((intervention: Intervention) => 
          selectedInterventionGroup
          .intervention_plan?.plan_items
            ?.some((planItem) =>
              planItem.intervention_id == intervention.id &&
              planItem.state == InterventionPlanStates.ACTIVE)) || [];

      let mergeWithActiveIntervention: Intervention[] =
        mergedInterventions
          .filter((inter: Intervention) => selectedLessonNotes.interventions.includes(inter.id))
          .concat(activeIntervention)

      return Object.values(
        mergeWithActiveIntervention.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {} as Record<number, Intervention>)
      );
    } else {
      return selectedInterventionGroup?.interventions.filter((intervention) => {
        if(selectedInterventionGroup.intervention_plan?.plan_items?.length) {
          return selectedInterventionGroup.intervention_plan?.plan_items.some((interventionPlan) => (interventionPlan.intervention_id == intervention.id) && (interventionPlan.state == InterventionPlanStates.ACTIVE))
        } else {
          return intervention;
        }
      }) ?? [];
    }
  }, [selectedInterventionGroup, selectedLessonNotes]);

  const [selectedNotesFor, setSelectedNotesFor] = useState<
    ReactSelectOption<number>[]
  >([]);

  const generalOption = useMemo(
    () => ({ value: ID_GENERAL_NOTES, label: "General Notes" }),
    []
  );

  let allStudent: number[] = [];
  selectedGroup.student_goals.map((goal) => {
    if (!goal.archived) {
      allStudent.push(goal.student.id!);
    }
  });

  const options: ReactSelectOption<number>[] = useMemo(() => {
    return [
      generalOption,
      ...(interventions?.map((int: Intervention) => ({
        value: int.id,
        label: int.name,
      })) || []),
    ];
  }, [interventions]);

  // Define stopListening function before it's used
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        // Ignore errors if recognition is already stopped
      }
    }

    setIsListening(false);
    setSpeechStatus("");
    setInterimText("");
  }, []);

  useEffect(() => {
    if (notes.length >= MAX_LENGTH_TEXT_AREA) {
      stopListening();
    }
  }, [notes, stopListening]);

  useEffect(() => {
    // Don't initialize if modal is not shown
    if (!show) {
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    // If SpeechRecognition API is available, enable it regardless of UA string
    // This allows it to work in Chrome DevTools device emulation mode
    if (!SpeechRecognition) {
      setMicSupported(false);
      return;
    }

    // Only disable for actual Firefox browsers (not emulated)
    if (isFirefox()) {
      setMicSupported(false);
      return;
    }

    // For Safari, check if SpeechRecognition is actually available
    // If it's available despite Safari UA, it means we're in Chrome DevTools emulation
    // Real Safari on iOS/iPadOS doesn't support SpeechRecognition API
    if (isSafari() && !SpeechRecognition) {
      setMicSupported(false);
      return;
    }

    // Enable for all devices that support SpeechRecognition (including Chrome on tablets and emulation mode)
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
      setSpeechStatus("Speak Now");
    };

    recognition.onspeechstart = () => {
      setSpeechStatus("Listening...");
    };

    recognition.onspeechend = () => {
      // Speech ended
    };

    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (
        let i = event.resultIndex;
        i < event.results.length;
        i++
      ) {
        const transcript =
          event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setInterimText(interimTranscript);

      if (finalTranscript) {
        setNotes(prev => {
          const updated =
            prev ? `${prev} ${finalTranscript}` : finalTranscript;

          const trimmed = updated.trim().slice(
            0,
            MAX_LENGTH_TEXT_AREA
          );
          
          return trimmed;
        });
      }
    };

    recognition.onerror = (event: any) => {
      // Handle specific errors
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        toastr.error("Microphone Access", "Please allow microphone access in your browser settings.");
      } else if (event.error === 'network') {
        toastr.error("Network Error", "Speech recognition requires an internet connection.");
      } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
        toastr.error("Speech Recognition Error", `Failed to recognize speech: ${event.error}`);
      }
      stopListening();
    };

    recognition.onend = () => {
      // Don't automatically stop if we're in continuous mode
      // The user should manually stop it
      setIsListening(false);
      setSpeechStatus("");
    };

    recognitionRef.current = recognition;
    setMicSupported(true);

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [stopListening, show]);

  const handleMicClick = () => {
    if (!micSupported) {
      return;
    }
    if (isMicDisabled) {
       stopListening();
      return;
    }

    if (!recognitionRef.current) {
      toastr.error("Error", "Speech recognition is not initialized. Please refresh the page.");
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        stopListening();
      }
    } else {
      try {
        recognitionRef.current.start();
      } catch (error: AxiosError | any) {
        if (error.message && error.message.includes('already started')) {
          // Recognition is already running, just update state
          setIsListening(true);
          setSpeechStatus("Listening...");
        } else {
          toastr.error("Error", "Failed to start speech recognition. Please check your microphone permissions.");
          stopListening();
        }
      }
    }
  };

  const handleSelectedNotesForChange = (
    values: any
  ) => {
    setSelectedNotesFor((values as any) ?? []);
  };

  const [tags, setTags] = useState<Omit<LessonNoteRecordTag, "order">[]>([]);
  const [selectedTags, setSelectedTags] = useState<
    Omit<LessonNoteRecordTag, "order">[]
  >([]);

  const handleCreateNextStep = (tag: string) => {
    const newTag = { type: LessonNoteRecordTagType.Custom, text: tag };
    setTags((tags) => [...tags, newTag]);
    setSelectedTags((tags) => [...tags, newTag]);
  };
  const handleChangeNextSteps = (
    options: any
  ) => {
    setSelectedTags((options as Omit<LessonNoteRecordTag, "order">[]) ?? []);
  };

  const [viewableBy, setViewableBy] = useState<number[]>([]);

  const myId = useSelector((s: ApplicationState) => s.auth.userInfo?.id);
  const loggedInUserInfo = useSelector((s: ApplicationState) => s.auth.userInfo);

  useEffect(() => {
    if (show) {
      if (selectedLessonNotes) {
        const notesForInterventions = selectedLessonNotes.interventions.map(
          (intId) => options.find((o) => o.value === intId)!
        );
        const notesFor = selectedLessonNotes.is_general
          ? [generalOption, ...notesForInterventions]
          : notesForInterventions;
        setNotes(selectedLessonNotes.notes);
        setSelectedNotesFor(notesFor);
        setSelectedDate(moment(selectedLessonNotes.date).toDate());
        setSelectedTags(selectedLessonNotes.record_tags);
        setViewableBy(selectedLessonNotes.viewable_by.filter((edu:any) => edu.id !== myId) || [myId!]);
        setAttachments(selectedLessonNotes.attachments ?? []);
      } else {
        setNotes("");
        setSelectedNotesFor([]);
        setSelectedDate(new Date());
        setSelectedTags([]);
        setViewableBy([myId!]);
        setAttachments([]);
      }
    }
  }, [show]);

  const checkAttendance = () => {
    if (showAntecedentForm) {
      //RC-832 stop the attandance api call
      //handleAttendanceAdd(); 
    }
      onHideForm(); 
  };

  const creationLoading = useSelector(
    (s: ApplicationState) => s.cases.isLoading.createLessonNotes
  );
  const creationError = useSelector(
    (s: ApplicationState) => s.cases.errors.createLessonNotes
  );
  const onCreationSuccess = useCallback(() => {
    toastr.success("Success", "Note is added successfully!");
    checkAttendance();
  }, [checkAttendance]);
  useLoading(creationLoading, creationError, onCreationSuccess);

  const updateLoading = useSelector(
    (s: ApplicationState) => s.cases.isLoading.updateLessonNotes
  );
  const updateError = useSelector(
    (s: ApplicationState) => s.cases.errors.updateLessonNotes
  );
  const onUpdateSuccess = useCallback(() => {
    toastr.success("Success", "Note is updated successfully!");
    checkAttendance();
  }, [checkAttendance]);
  useLoading(updateLoading, updateError, onUpdateSuccess);

  const createAttendanceLoading = useSelector(
    (s: ApplicationState) => s.cases.isLoading.createAttendance
  );
  const createAttendanceError = useSelector(
    (s: ApplicationState) => s.cases.errors.createAttendance
  );
  const onCreateAttendanceSuccess = useCallback(() => {
    //toastr.success("Success", "Attendance is added successfully!");
    onHideForm();
  }, []);
  useLoading(
    createAttendanceLoading,
    createAttendanceError,
    onCreateAttendanceSuccess
  );

  const handleAttendanceAdd = () => {
    dispatch(
      createAttendance(selectedInterventionGroup?.id!, {
        date: selectedDate!.toISOString(),
        interventions: selectedNotesFor
          .filter((o) => o.value !== ID_GENERAL_NOTES)
          .map((o) => o.value),
        absent_students:
          allStudent
            .filter((st) => !selectedStudents.includes(st))
            .map((st) => st) ?? [],
        present_students: selectedStudents,
      })
    );
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
     if (selectedInterventionGroup?.id && selectedDate) {
      // if(!justMeChecked && !viewableBy.length) {
      //   toastr.error("This notes for:", "Please select for whom this note is intended.");
      //   return;
      // }
      // if(showAntecedentForm && !selectedStudents.length) {
      //   toastr.error("Alert!!", "Please select the student or switch off TAG STUDENT TO NOTES.");
      //   return;
      // }
      const body: Omit<
        LessonNotes,
        "id" | "intervention_group_id" | "user" | "attachments" | "group"
      > & {
        attachments: number[];
      } = {
        date: selectedDate.toISOString(),
        notes: notes,
        interventions: selectedNotesFor
          .filter((o) => o.value !== ID_GENERAL_NOTES)
          .map((o) => o.value),
        is_general: selectedNotesFor.some((o) => o.value === ID_GENERAL_NOTES),
        record_tags: selectedTags.map((tag, index) => ({
          ...tag,
          order: index,
        })),
        viewable_by: _.union([...viewableBy, myId!]),
        attachments: attachments.map((a) => a.id!),
        pertaining_students: showAntecedentForm ? selectedStudents : []
      };
      

      if (selectedLessonNotes) {
        dispatch(
          updateLessonNotes(selectedInterventionGroup.id, {
            id: selectedLessonNotes.id,
            ...body,
          })
        );
      } else {
        dispatch(createLessonNotes(selectedInterventionGroup.id, body));
      }
    }
  };

  const defaultTags: Omit<LessonNoteRecordTag, "order">[] = useMemo(() => {
    return Object.values(LessonNoteRecordTagType)
      .filter((value) => value !== LessonNoteRecordTagType.Custom)
      .map((value) => ({
        type: value,
        text: LessonNoteRecordTagTypeDisplayedValues[value],
      }));
  }, []);

  

  const allEducators: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>[] = useMemo(() => {
    let allUsers: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>[] = [];
    if(Object.keys(selectedGroup.teacher_assignment!).length) {
      allUsers.push({
        id: selectedGroup.teacher_assignment?.user.id!,
        first_name: selectedGroup.teacher_assignment?.user.first_name!,
        last_name: selectedGroup.teacher_assignment?.user.last_name!,
      })
    }
    if(Object.keys(selectedGroup.coach_assignment!).length) {
      allUsers.push({
        id: selectedGroup.coach_assignment?.user.id!,
        first_name: selectedGroup.coach_assignment?.user.first_name!,
        last_name: selectedGroup.coach_assignment?.user.last_name!,
      })
    }
    if(selectedGroup.associated_assignments.length) {
      selectedGroup.associated_assignments.forEach((associate) => {
        return allUsers.push({
          id: associate.user.id!,
          first_name: associate.user.first_name!,
          last_name: associate.user.last_name!,
        })
      }
      )
    }
    return Object.values(
      allUsers.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {})
    )
  },[selectedGroup.teacher_assignment, selectedGroup.coach_assignment, selectedGroup.additional_educator_plan])

  
  const justMeChecked = useMemo(
    () =>  (viewableBy.length === 1 && viewableBy.includes(myId!)),
    [viewableBy, myId]
  );

  const allGroupChecked = useMemo(() => {
    const groupUsers = allEducators.filter((edu: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => edu.id !== myId);

    return (
      groupUsers.length > 0 &&
      groupUsers.every((educator: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) =>
        viewableBy.includes(educator.id)
      )
    );
  }, [viewableBy, allEducators, myId]);

  const isGroupIndeterminate = useMemo(() => {
    const groupUsers = allEducators.filter((edu: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => edu.id !== myId);

    const selectedCount = groupUsers.filter((educator: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) =>
      viewableBy.includes(educator.id)
    ).length;

    return (
      selectedCount > 0 &&
      selectedCount < groupUsers.length
    );
  }, [viewableBy, allEducators, myId]);

  const groupCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (groupCheckboxRef.current) {
      groupCheckboxRef.current.indeterminate = isGroupIndeterminate;
    }
  }, [isGroupIndeterminate]);

  useEffect(() => {
    setTags(defaultTags);
  }, [defaultTags]);

  const handleViewableByChange = (userId: number) => (
  e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;

    setViewableBy((prev) => {
      const updated = checked
        ? [...prev, userId]
        : prev.filter((id) => id !== userId);

      // if nothing selected from group -> auto select self
      const groupUsers = allEducators
        .filter((edu: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => edu.id !== myId)
        .map((edu: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => edu.id);

      const hasAnyGroupSelected = updated.some((id) =>
        groupUsers.includes(id)
      );

      return hasAnyGroupSelected ? updated : [myId!];
    });
  };


  const [showAntecedentForm, setShowAntecedentForm] = useState(true);

  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);

  const [attachments, setAttachments] = useState<AttachedResource[]>([]);

  const handleNotes = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const inputValue = e.target.value;
    const nativeEvent = e.nativeEvent as InputEvent;

    if (inputValue.length > MAX_LENGTH_TEXT_AREA) {
      if (
        nativeEvent.inputType === "insertFromPaste"
      ) {
        toastr.info(
          "Info",
          `You’ve exceeded the 3000-character limit. Any extra text has been removed.`
        );
      }

      if (nativeEvent.inputType === "insertText") {
        toastr.error(
          "Alert",
          `You’ve exceeded the 3000-character limit.`
        );
      }
      stopListening();
    }

    const trimmedValue = inputValue.slice(
      0,
      MAX_LENGTH_TEXT_AREA
    );

    setNotes(trimmedValue);

    if (
      trimmedValue.length >= MAX_LENGTH_TEXT_AREA &&
      recognitionRef.current &&
      isListening
    ) {
      stopListening();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="lesson-notes-upsert-form workspace-content-container">
      <form id={"lesson-notes-upsert-form "} onSubmit={handleFormSubmit}>
        <Form.Group className="mb-2">
          <Form.Label className="font-weight-bold d-block">Date</Form.Label>
          <DatePicker
            required
            customInput={<CustomDateBoxInput />}
            selected={selectedDate}
            onChange={setSelectedDate}
          />
        </Form.Group>
        <Form.Group className="my-2">
          <Form.Label>
            <span className="font-weight-bold">Intervention(s)</span>
            <span className="text-muted small"> (Optional) </span>
          </Form.Label>
          <Select
            isMulti
            name="notesFor"
            value={selectedNotesFor}
            options={options as any}
            onChange={handleSelectedNotesForChange}
            placeholder="Select"
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 1070 }) }}
            menuPortalTarget={document.body}
            // required={showAntecedentForm ? true : false}
          />
        </Form.Group>
        <Form.Group className="my-2">
          <Form.Label>
            <span className="font-weight-bold">Records & Next Steps</span>
            <span className="text-muted small"> (Optional) </span>
          </Form.Label>
          <CreatableSelect
            isMulti
            isClearable
            value={selectedTags}
            options={tags as any}
            createOptionPosition="first"
            placeholder="Select or create a tag"
            onCreateOption={handleCreateNextStep}
            onChange={handleChangeNextSteps}
            getOptionLabel={(tag: any) => tag.label ?? tag.text}
            getOptionValue={(tag: any) => tag.value ?? tag.text}
            getNewOptionData={(inputValue, optionLabel) =>
              ({
                label: optionLabel,
                value: inputValue,
              } as any)
            }
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1070 }) }}
            menuPortalTarget={document.body}
            menuPosition={"absolute"}
            menuPlacement={"auto"}
          />
        </Form.Group>
        <Form.Group className="my-2">
          <Form.Label>
            <span className="font-weight-bold">Notes</span>
            <span className="text-muted small"> (Optional) </span>
          </Form.Label>
            <Form.Control
              as="textarea"
              className="myNotesTextArea"
              value={notes + (interimText ? ' ' + interimText : '')}
              onChange={handleNotes}
              placeholder="Enter notes..."
              style={{ scrollbarWidth: "thin" }}
            />

          <div className="notesFooter">
            <div className="speechSection">
              {isListening && (
                <span className="speechStatus">
                  {speechStatus}
                  {interimText && <span className="text-muted ml-2">(recognizing...)</span>}
                </span>
              )}

              {!micSupported ? (
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip className="customTooltip" id="mic-tooltip">
                      Speech recognition is not supported in this browser. Please use Google Chrome on desktop.
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className={`micButton disabled`}
                    onClick={handleMicClick}
                    disabled
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                </OverlayTrigger>
              ) : (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip className="customTooltip" id="mic-tooltip">
                      {isMicDisabled 
                        ? "Character limit reached" 
                        : isListening 
                        ? "Click to stop recording" 
                        : "Click to start voice input"}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className={`micButton ${
                      isListening && !isMicDisabled
                        ? "listening"
                        : ""
                    }`}
                    onClick={handleMicClick}
                    disabled={isMicDisabled}
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                </OverlayTrigger>
              )}
            </div>

            <div className="characterCount">
              {notes.length}/{MAX_LENGTH_TEXT_AREA}
            </div>
          </div>
        </Form.Group>
        <hr />
        <div className="mt-3 row col-8">
          <Form.Label className="font-weight-bold mr-2">Tag student to notes</Form.Label>
          <Form.Check
            type="switch"
            id="tag_student_to_notes"
            checked={showAntecedentForm}
            onClick={(e) => {
              setShowAntecedentForm((s) => !s);
              showAntecedentForm ? setSelectedStudents(allStudent) : '';
            }}
          />
        </div>
        <hr />
        {showAntecedentForm ? (
          <>
            <AttendanceStudentsList
              students={selectedInterventionGroup?.students ?? []}
              selectedStudents={selectedStudents}
              setSelectedStudents={setSelectedStudents}
              intervention={selectedInterventionGroup}
              selectedLessonNotes= {selectedLessonNotes}
            />
            <hr />
          </>
        ) : ''}
        <Form.Group className="my-2">
          <Form.Label className="mb-1">
            <span className="font-weight-bold">Note is for</span>
            <span className="text-muted small"> (Optional) </span>
          </Form.Label>
          <Form.Check
            id={"just-me-checkbox"}
            custom
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setViewableBy(e.target.checked ? [myId!] : []);
              if (groupCheckboxRef.current) {
                groupCheckboxRef.current.indeterminate = false;
              }
            }}
            className="customDefaultCheckbox mb-2"
            type="checkbox"
            label={
              <label htmlFor="selfCheckbox" style={{ cursor: "pointer", marginBottom: 0, width: "100%" }}>
                Self (Me)
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip className="customTooltip" id="tooltip-top">
                      This note is visible only for me.
                    </Tooltip>
                  }
                >
                  <span className="ml-2" style={{ cursor: "pointer" }}>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="purpleText"
                    />
                  </span>
                </OverlayTrigger>
              </label>
            }
            checked={justMeChecked}
          />
     
          <Form.Check
            id={"group-checkbox"}
            ref={groupCheckboxRef as any}
            custom
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;

              if (checked) {
                setViewableBy(
                  allEducators
                    .filter((edu: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => edu.id !== myId)
                    .map((educator: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => educator.id)
                );
              } else {
                // auto select Self(Me)
                setViewableBy([myId!]);
              }
            }}
            className="customDefaultCheckbox"
            type="checkbox"
            label={
              <label htmlFor="selfCheckbox" style={{ cursor: "pointer", marginBottom: 0, width: "100%" }}>
                Group
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip className="customTooltip" id="tooltip-top">
                      This note is shared with the group members listed below.
                    </Tooltip>
                  }
                >
                  <span className="ml-2" style={{ cursor: "pointer" }}>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="purpleText"
                    />
                  </span>
                </OverlayTrigger>
              </label>
            }
            checked={allGroupChecked}
          />
          {allEducators
            .filter((associateAssignment: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => associateAssignment.id != myId)
            .map((aa: Pick<UserInfo, 'id' | 'first_name' | 'last_name'>) => {
              return (
                <Form.Check
                  id={`associated-user-${aa.id}-checkbox`}
                  key={`associated-user-${aa.id}-checkbox`}
                  custom
                  onChange={handleViewableByChange(aa.id)}
                  className="customDefaultCheckbox ml-4"
                  type="checkbox"
                  label={getFullName(aa)}
                checked={viewableBy.includes(aa.id)}
              />
            );
          })}
        </Form.Group>
      </form>
      <hr />
      <AddResourcesForm
        attachments={attachments}
        onAttachmentsAdd={(resources) =>
          setAttachments((attachments) => [...attachments, ...resources])
        }
        onAttachmentRemove={(resourceId) =>
          setAttachments((attachments) =>
            attachments.filter((a) => a.id !== resourceId)
          )
        }
      />
      <hr />
      <div className="d-flex justify-content-between">
        <button className="whiteBtnSm" onClick={onHideForm}>
          Cancel
        </button>
        <button
          className="blueBtnSm"
          type="submit"
          form="lesson-notes-upsert-form "
        >
          Save{" "}
          {(creationLoading || updateLoading || createAttendanceLoading) && (
            <Spinner animation="border" size="sm" className="ml-1" />
          )}
        </button>
      </div>
    </div>
  );
};

export default LessonNotesUpsertForm;
